import { PiiWhitelist } from "@/models/pii/PiiWhitelist";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { restError } from "@/models/client/RestResponse";

/**
 * Per-tenant PII whitelist (LAR-27). The query feeds the client detection
 * engine (soft matches whose normalised text is whitelisted are dropped); the
 * mutations back the admin settings UI. Reading is open to any tenant user;
 * the API gates the mutations behind the ManagePiiSettings permission.
 */
const usePiiWhitelist = () => {
  const queryClient = useQueryClient();
  const service = useItemService((services) => services.piiWhitelist);

  /** `enabled: false` skips the fetch (chat passes the PII-Redaction flag). */
  const useQueryWhitelist = (enabled: boolean = true) =>
    useQuery<PiiWhitelist>({
      queryKey: [PATH_KEYS.piiWhitelist],
      queryFn: async () => {
        const response = await service.get();
        if (response.success) return response.value;
        throw restError(response);
      },
      staleTime: 300_000,
      retry: false,
      enabled,
    });

  const addTerm = useMutation<PiiWhitelist, Error, string>({
    mutationKey: [PATH_KEYS.piiWhitelist, "add"],
    mutationFn: async (term: string) => {
      const response = await service.addTerm(term);
      if (response.success) return response.value;
      throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.piiWhitelist] });
    },
  });

  const removeTerm = useMutation<PiiWhitelist, Error, string>({
    mutationKey: [PATH_KEYS.piiWhitelist, "remove"],
    mutationFn: async (normalized: string) => {
      const response = await service.removeTerm(normalized);
      if (response.success) return response.value;
      throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.piiWhitelist] });
    },
  });

  return { useQueryWhitelist, addTerm, removeTerm };
};

export default usePiiWhitelist;
